/**
 * Geometry for user drawings.
 *
 * Everything here is pure and works in PIXELS. Drawings are stored in
 * (time, price) so they stay pinned to the chart through pan and zoom; the
 * primitive converts to pixels once per frame and hands the result to these
 * functions. Keeping the maths out of the renderer means the hit-testing and
 * the drawing use the same geometry rather than two implementations that can
 * disagree — the bug class that made the gamma flip band-dependent.
 */

/** Tools available. Anchor counts differ per tool — see ANCHOR_COUNT. */
export type DrawingTool = 'line' | 'ray' | 'horizontal' | 'channel';

/** A drawing is anchored in chart space, never pixels. */
export interface DrawingAnchor {
  /** Bar time, seconds since epoch (lightweight-charts UTCTimestamp). */
  time: number;
  price: number;
}

export interface Drawing {
  id: string;
  tool: DrawingTool;
  anchors: DrawingAnchor[];
  createdAt: number;
}

/** How many clicks each tool needs before it is complete. */
export const ANCHOR_COUNT: Record<DrawingTool, number> = {
  line: 2,
  ray: 2,
  horizontal: 1,
  // Two for the base segment, a third to set the parallel offset.
  channel: 3,
};

export const TOOL_LABELS: Record<DrawingTool, string> = {
  line: 'Line',
  ray: 'Ray',
  horizontal: 'Horizontal',
  channel: 'Channel',
};

export interface PixelPoint {
  x: number;
  y: number;
}

export interface Bounds {
  width: number;
  height: number;
}

/** Pixels of slop around a drawing that still count as a hit. */
export const HIT_TOLERANCE = 6;

/** Half-width of a selection handle square. */
export const HANDLE_RADIUS = 4;

/**
 * Shortest distance from a point to a segment.
 *
 * Clamps the projection to [0,1] so the ends are treated as caps rather than
 * an infinite line — without it, a point far off the end of a short trendline
 * would register as a hit.
 */
export function distanceToSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

/**
 * Where a ray from `a` through `b` leaves the viewport.
 *
 * Rays are defined by two anchors but drawn to the edge, so the far end is
 * geometry rather than stored state — it has to be recomputed whenever the
 * pane resizes or the user zooms.
 */
export function extendRay(a: PixelPoint, b: PixelPoint, bounds: Bounds): PixelPoint {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (dx === 0 && dy === 0) return b;

  // Largest t with a + t*d still inside the box. Each axis contributes a limit;
  // the binding one is the smaller.
  let t = Infinity;
  if (dx > 0) t = Math.min(t, (bounds.width - a.x) / dx);
  else if (dx < 0) t = Math.min(t, (0 - a.x) / dx);
  if (dy > 0) t = Math.min(t, (bounds.height - a.y) / dy);
  else if (dy < 0) t = Math.min(t, (0 - a.y) / dy);

  if (!Number.isFinite(t) || t < 1) t = 1; // never pull the ray back inside its own anchors
  return { x: a.x + dx * t, y: a.y + dy * t };
}

/**
 * The polylines that make up a drawing, in draw order.
 *
 * One function for both rendering and hit-testing. A channel is two parallel
 * segments; a horizontal spans the pane; a ray runs to the edge.
 */
export function drawingSegments(
  tool: DrawingTool,
  pts: readonly PixelPoint[],
  bounds: Bounds
): PixelPoint[][] {
  switch (tool) {
    case 'line':
      return pts.length >= 2 ? [[pts[0], pts[1]]] : [];
    case 'ray':
      return pts.length >= 2 ? [[pts[0], extendRay(pts[0], pts[1], bounds)]] : [];
    case 'horizontal':
      return pts.length >= 1
        ? [[{ x: 0, y: pts[0].y }, { x: bounds.width, y: pts[0].y }]]
        : [];
    case 'channel': {
      if (pts.length < 2) return [];
      const base: PixelPoint[] = [pts[0], pts[1]];
      if (pts.length < 3) return [base];
      // Second rail passes through the third anchor, parallel to the base:
      // translate the base segment by (p2 - p0).
      const dx = pts[2].x - pts[0].x;
      const dy = pts[2].y - pts[0].y;
      return [
        base,
        [
          { x: pts[0].x + dx, y: pts[0].y + dy },
          { x: pts[1].x + dx, y: pts[1].y + dy },
        ],
      ];
    }
    default:
      return [];
  }
}

/** Index of the handle under the cursor, or -1. */
export function handleAt(pts: readonly PixelPoint[], px: number, py: number): number {
  for (let i = 0; i < pts.length; i++) {
    if (Math.abs(px - pts[i].x) <= HANDLE_RADIUS + 2 && Math.abs(py - pts[i].y) <= HANDLE_RADIUS + 2) {
      return i;
    }
  }
  return -1;
}

/** Whether the cursor is on the drawing itself (not a handle). */
export function isOnDrawing(
  tool: DrawingTool,
  pts: readonly PixelPoint[],
  bounds: Bounds,
  px: number,
  py: number,
  tolerance: number = HIT_TOLERANCE
): boolean {
  for (const seg of drawingSegments(tool, pts, bounds)) {
    for (let i = 0; i + 1 < seg.length; i++) {
      if (distanceToSegment(px, py, seg[i].x, seg[i].y, seg[i + 1].x, seg[i + 1].y) <= tolerance) {
        return true;
      }
    }
  }
  return false;
}
